/**
 * Accepting an account invitation (or a recovery link): choose a password.
 * The link works once. Two-step verification, if the account requires it,
 * is set up right after the first sign-in.
 */

import { useEffect, useState } from 'react'
import { ApiError, request } from '@/lib/api'
import { Card, Skeleton } from '@/components/ui'

export function AcceptInvitationPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [info, setInfo] = useState<{ firstName: string; username: string; portal: string; recovery: boolean; expiresAt: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [done, setDone] = useState<{ username: string; mfaEnrollmentRequired: boolean } | null>(null)
  useEffect(() => { request<NonNullable<typeof info>>('/auth/invitation', { query: { token }, anonymous: true }).then(setInfo).catch((e) => setError(e instanceof ApiError ? e.message : 'This link could not be checked.')) }, [token])
  const submit = async () => {
    setError(null)
    if (pw !== pw2) { setError('The two passwords do not match.'); return }
    try { setDone(await request<{ username: string; mfaEnrollmentRequired: boolean }>('/auth/invitation', { method: 'POST', body: { token, password: pw }, anonymous: true })) } catch (e) { setError(e instanceof ApiError ? e.message : 'Your password was not saved.') }
  }
  return (
    <div className="boot">
      <Card title={info?.recovery ? 'Choose a new password' : 'Set up your account'}>
        {error && !info ? <p className="support__error" role="alert">{error}</p> : !info ? <Skeleton rows={3} label="Checking your link" /> : done ? (
          <>
            <p>All set. Sign in as <strong>{done.username}</strong> with your new password.{done.mfaEnrollmentRequired ? ' You will set up two-step verification next.' : ''}</p>
            <a className="button button--primary" href="/">Sign in</a>
          </>
        ) : (
          <>
            <p>Hello {info.firstName}. Your username is <strong>{info.username}</strong>. Choose a password of at least 12 characters.</p>
            <label>New password <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></label>
            <label>Again <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
            {error && <p className="support__error" role="alert">{error}</p>}
            <p><button type="button" className="button button--primary" disabled={pw.length < 12} onClick={() => void submit()}>Save password</button></p>
            <p className="support__hint">This link works once and expires {new Date(info.expiresAt).toLocaleString()}.</p>
          </>
        )}
      </Card>
    </div>
  )
}
